const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const formatEmbarkDisembarkDate = (
  embarkationDate: string,
  disEmbarkationDate: string,
): string => {
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };

  const formattedEmbarkationDate = new Intl.DateTimeFormat(
    'en-US',
    options,
  ).formatToParts(new Date(embarkationDate));
  const formattedDisembarkationDate = new Intl.DateTimeFormat(
    'en-US',
    options,
  ).formatToParts(new Date(disEmbarkationDate));

  const startDay = formattedEmbarkationDate.find(
    part => part.type === 'day',
  )?.value;
  const endDay = formattedDisembarkationDate.find(
    part => part.type === 'day',
  )?.value;

  const endMonth = formattedDisembarkationDate.find(
    part => part.type === 'month',
  )?.value;

  const endYear = formattedDisembarkationDate.find(
    part => part.type === 'year',
  )?.value;

  return `${startDay} - ${endDay} ${endMonth} ${endYear}`;
};

export type DateTimeString = string; // '06/13/2024 00:00:00'

export function formatDateRange(
  embarkation: DateTimeString | undefined,
  disembarkation: DateTimeString | undefined,
  includeYear: boolean = false,
): string {
  if (!embarkation || !disembarkation) {
    return '';
  }
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // Parse dates as local dates to avoid timezone issues
  // If the date string is in YYYY-MM-DD format, parse it as local date
  const parseLocalDate = (dateString: string): Date => {
    // Check if it's in YYYY-MM-DD format (ISO date format)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed in JS
    }
    // Otherwise, parse normally
    return new Date(dateString);
  };

  const embarkDate = parseLocalDate(embarkation);
  const disembarkDate = parseLocalDate(disembarkation);

  const embarkDay = embarkDate.getDate();
  const disembarkDay = disembarkDate.getDate();

  const embarkMonth = monthNames[embarkDate.getMonth()];
  const disembarkMonth = monthNames[disembarkDate.getMonth()];

  const embarkYear = embarkDate.getFullYear();
  const disembarkYear = disembarkDate.getFullYear();

  if (embarkMonth === disembarkMonth) {
    return `${embarkDay} - ${disembarkDay} ${embarkMonth} ${
      includeYear ? embarkYear : ''
    }`;
  } else {
    return `${embarkDay} ${embarkMonth} - ${disembarkDay} ${disembarkMonth} ${
      includeYear ? disembarkYear : ''
    }`;
  }
}

export const getMonthName = (monthId: number) => {
  return monthNames[monthId - 1]; // Subtract 1 to account for zero-based index
};
export const formatTo12HourTimeOnly = (bkkTimeString: string): string => {
  // Assuming the input time string is in "YYYY-MM-DDTHH:MM:SS" format and is already in BKK time

  // Create a date object without converting to local time
  // Split the date and time parts
  const parts = bkkTimeString.split(/[-T:]/);
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // JS months are zero-indexed
  const day = parseInt(parts[2], 10);
  const hours = parseInt(parts[3], 10);
  const minutes = parseInt(parts[4], 10);

  // Create a date object with BKK time assumed as local time
  const bkkDate = new Date(year, month, day, hours, minutes);

  // Format hours for 12-hour format
  let hours12 = bkkDate.getHours() % 12;
  hours12 = hours12 || 12; // Convert 0 to 12 for 12AM
  const ampm = bkkDate.getHours() >= 12 ? 'PM' : 'AM';

  // Format minutes with leading zero
  const formattedMinutes =
    bkkDate.getMinutes() < 10
      ? '0' + bkkDate.getMinutes()
      : bkkDate.getMinutes().toString();

  // Construct the format
  const strTime = `${hours12}:${formattedMinutes} ${ampm}`;
  return strTime;
};

export const formatDate = (dateStr: string): string => {
  if (!dateStr?.trim()) {
    return '';
  }
  const firstPart = dateStr.split(' ')[0];

  // GraphQL / API dates are typically YYYY-MM-DD (same as formatDateRange)
  if (/^\d{4}-\d{2}-\d{2}$/.test(firstPart)) {
    const [y, m, d] = firstPart.split('-').map(Number);
    const monthLabel = monthNames[m - 1];
    if (!monthLabel || Number.isNaN(d) || Number.isNaN(y)) {
      return '';
    }
    return `${d} ${monthLabel} ${y}`;
  }

  // Legacy MM/DD/YYYY (optional time after space)
  const dateParts = firstPart.split('/');
  if (dateParts.length !== 3) {
    return '';
  }
  const year = dateParts[2];
  const monthIndex = parseInt(dateParts[0], 10) - 1;
  const day = parseInt(dateParts[1], 10);
  if (
    Number.isNaN(monthIndex) ||
    Number.isNaN(day) ||
    monthIndex < 0 ||
    monthIndex > 11
  ) {
    return '';
  }
  return `${day} ${monthNames[monthIndex]} ${year}`;
};

export function formatInputDate(dateString: string | undefined) {
  if (!dateString) {
    return '';
  }
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // month starts at 0
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const formatTime = (datetime: string) => {
  const date = new Date(datetime);
  const hours = date.getHours().toString().padStart(2, '0'); // Ensure two-digit format
  const minutes = date.getMinutes().toString().padStart(2, '0'); // Ensure two-digit format
  return `${hours}:${minutes}`;
};
